/**
 * Created by Jonas Wålekvist on 2018-06-27.
 */

import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {SlotGame} from "@nolimitcity/slot-game/bin/core/SlotGame";
import {Orientation} from "@nolimitcity/slot-game/bin/core/stage/Orientation";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Linear, TimelineLite, TweenLite} from "gsap";
import {DungeonQuestLayerConfig} from "../../../config/DungeonQuestLayerConfig";
import {MaskCreator} from "../../../utils/MaskCreator";
import {DungeonQuest} from "../../DungeonQuest";
import {DungeonQuestGameAssets} from "../../DungeonQuestGameAssets";
import {DungeonQuestGameConfig, DungeonQuestSymbols} from "../../DungeonQuestGameConfig";
import {DungeonQuestSound} from "../../DungeonQuestSound";
import {FreeSpinIntroSymbol} from "./FreeSpinIntroSymbol";


enum SymbolIndex {
    L1 = 3,
    L2 = 2,
    L3 = 1,
    L4 = 0
}

export class DungeonQuestFreeSpinIntroView extends BaseView {
    private _showing:boolean;
    private _layer:ScalableLayer;
    private _symbols:FreeSpinIntroSymbol[];
    private _currentIndex:number = 0;
    private _symbolsContainer:PIXI.Container;
    private _introBase:TimelineSprite;
    private _introLightning1:TimelineSprite;
    private _introLightning2:TimelineSprite;
    private _introLightning3:TimelineSprite;
    private _introSmoke1:TimelineSprite;
    private _introSmoke2:TimelineSprite;
    private _viewContainer:PIXI.Container;
    private _ring:PIXI.Sprite;
    private _viewMask:PIXI.Graphics;
    private _symbolLayer:ScalableLayer;


    constructor() {
        super();
        this._layer = StageManager.getLayer(DungeonQuestLayerConfig.RANDOM_FEATURE_INTRO.name);
        this._layer.addChild(this);
        this._symbolLayer = StageManager.getLayer(DungeonQuestLayerConfig.SYMBOLS.name);
    }


    protected onResize(resizeData:IResizeData):void {
        const height = (resizeData.bottom / this._layer.scale.y) - this._layer.position.y;
        this._viewContainer.y = height * 0.45;

        if(resizeData.orientation == Orientation.PORTRAIT) {
            this._viewMask.width = Math.ceil(resizeData.width / this._symbolLayer.scale.x);
        } else {
            this._viewMask.scale.x = 1;
        }
    }

    protected initAnimations() {
        this._viewMask = MaskCreator.createSymbolAreaMaskGraphics();
        this._viewMask.pivot.set(this._viewMask.width * 0.5, 0);
        this._viewMask.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, 0);


        this.alpha = 0;
        this._showing = false;

        this._viewContainer = new PIXI.Container();
        this._viewContainer.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT * 0.5);

        const distanceFromCenter:number = 180;
        const symbolScale:number = 1.6;


        this._introBase = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.FREE_SPIN_INTRO_BASE));
        this._introBase.anchor.set(0.5, 0.47);

        this._introLightning1 = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.FREE_SPIN_INTRO_LIGHTNING));
        this._introLightning2 = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.FREE_SPIN_INTRO_LIGHTNING));
        this._introLightning3 = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.FREE_SPIN_INTRO_LIGHTNING));

        this._introLightning1.rotation = 0;
        this._introLightning2.rotation = Math.PI / 1.5;
        this._introLightning3.rotation = Math.PI / 0.75;

        this._introLightning1.anchor.set(0.8, 0.5);
        this._introLightning2.anchor.set(0.8, 0.5);
        this._introLightning3.anchor.set(0.8, 0.5);

        this._introSmoke1 = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.FREE_SPIN_INTRO_SMOKE));
        this._introSmoke1.anchor.set(0.3, 0.41);

        this._introSmoke2 = new TimelineSprite(GameResources.getTextures(DungeonQuestGameAssets.FREE_SPIN_INTRO_SMOKE));
        this._introSmoke2.anchor.set(0.3, 0.41);
        this._introSmoke2.rotation = Math.PI;

        this._introBase.setFrame(0);
        this._introLightning1.setFrame(0);
        this._introLightning2.setFrame(0);
        this._introLightning3.setFrame(0);
        this._introSmoke1.setFrame(5);
        this._introSmoke2.setFrame(5);


        this._symbols = [
            new FreeSpinIntroSymbol(DungeonQuestSymbols.L4),
            new FreeSpinIntroSymbol(DungeonQuestSymbols.L3),
            new FreeSpinIntroSymbol(DungeonQuestSymbols.L2),
            new FreeSpinIntroSymbol(DungeonQuestSymbols.L1)
        ];

        this._symbolsContainer = new PIXI.Container();
        for(let symbol of this._symbols) {
            symbol.scale.set(symbolScale);
            this._symbolsContainer.addChild(symbol);
        }


        this._symbols[SymbolIndex.L4].position.set(0, -distanceFromCenter);
        this._symbols[SymbolIndex.L3].position.set(distanceFromCenter, 0);
        this._symbols[SymbolIndex.L2].position.set(0, distanceFromCenter);
        this._symbols[SymbolIndex.L1].position.set(-distanceFromCenter, 0);

        this._ring = new PIXI.Sprite(GameResources.getTextures(DungeonQuestGameAssets.TRANSFORM_INTRO_RING)[0]);
        this._ring.anchor.set(0.5, 0.5);
        this._ring.position.set(DungeonQuestGameConfig.instance.REEL_AREA_WIDTH * 0.5, DungeonQuestGameConfig.instance.REEL_AREA_HEIGHT * 0.5 + 150);
        this._ring.alpha = 0;


        this._viewContainer.addChild(this._introBase);
        this._viewContainer.addChild(this._introLightning1);
        this._viewContainer.addChild(this._introLightning2);
        this._viewContainer.addChild(this._introLightning3);
        this._viewContainer.addChild(this._introSmoke1);
        this._viewContainer.addChild(this._introSmoke2);
        this._viewContainer.addChild(this._ring);
        this._viewContainer.addChild(this._symbolsContainer);

        this.addChild(this._viewContainer);
        this._symbolLayer.addChild(this._viewMask);
        this.mask = this._viewMask;
        this.setUpForIntro();

    }

    public show():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(!this._showing) {
            this._showing = true;
            tl.add([
                DungeonQuest.reelAreaEffectController.fadeInDarkenPlate(0.5),
                new TweenLite(this, 0.5, {alpha : 1, ease : Linear.easeNone})
            ]);
        }
        return tl;
    }

    public hide():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        if(this._showing) {
            this._showing = false;
            tl.add([
                DungeonQuest.reelAreaEffectController.fadeOutDarkenPlate(0.5),
                new TweenLite(this, 0.5, {alpha : 0, ease : Linear.easeNone})
            ]);
        }
        return tl;
    }


    public onRotationUpdate() {
        for(let symbol of this._symbols) {
            symbol.rotation = -this._symbolsContainer.rotation;
        }
    }

    private setUpForIntro() {
        this._symbolsContainer.scale.set(0);
        this._symbolsContainer.rotation = 0;
        this._introBase.hide();
        this._introLightning1.hide();
        this._introLightning2.hide();
        this._introLightning3.hide();
        this._introSmoke1.hide();
        this._introSmoke2.hide();
    }

    public getIntroAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        this.setUpForIntro();


        const startAnim = new TimelineLite();
        startAnim.add(this._introBase.getAnimationAutoShowHide(true, true), 0);

        const lightning = new TimelineLite();
        lightning.add(this._introLightning1.getAnimationAutoShowHide(true, true), 0);
        lightning.add(this._introLightning2.getAnimationAutoShowHide(true, true), 0.1);
        lightning.add(this._introLightning3.getAnimationAutoShowHide(true, true), 0.2);

        const smokeAnim = new TimelineLite();
        smokeAnim.add([
            this._introSmoke1.getAnimationAutoShowHide(true, true),
            this._introSmoke2.getAnimationAutoShowHide(true, true)
        ]);


        const symbolIntro = new TimelineLite();
        for(let symbol of this._symbols) {
            symbol.setUpForIntro();
            symbolIntro.add(symbol.getFromWhiteAnimation(0.6), 0);
        }

        const customEase = CustomEase.create("custom", "M0,0 C0.028,0.382 0.112,0.674 0.27,0.822 0.462,1.002 0.818,1 1,1");

        tl.add(startAnim);
        tl.add([
            smokeAnim,
            lightning,
            symbolIntro,
            new TweenLite(this._symbolsContainer.scale, smokeAnim.totalDuration() + 0.1, {
                x : 1,
                y : 1,
                ease : customEase
            }),
            new TweenLite(this._symbolsContainer, smokeAnim.totalDuration() + 0.1, {
                rotation : 4 * Math.PI,
                ease : customEase,
                onUpdate : () => this.onRotationUpdate()
            })
        ], 0.8);

        return tl;
    }

    public setSymbolsToCorrectStateBeforeUpdate() {
        for(let i:number = 0; i < this._currentIndex; i++) {
            const symbol = this._symbols[i];
            symbol.setToEndState();
        }
    }

    private getSymbolUpdateAnimation(symbol:FreeSpinIntroSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        tl.add(symbol.getAttention(0.5));
        tl.add(symbol.getToWhiteAnimation(0.15));
        tl.add(symbol.getTransform());
        return tl;
    }

    public getUpdateSymbolAnimation():TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        const symbol = this._symbols[this._currentIndex];
        tl.add(() => SlotGame.sound.playEffect(DungeonQuestSound.FREE_SPIN_INTRO_SYMBOL_CHANGE));
        tl.add([
            () => this._symbolsContainer.scale.set(1),
            () => this._symbolsContainer.rotation = 0
        ]);
        tl.add([
            this.darkenNonActiveSymbols(0.2),
            this.getSymbolUpdateAnimation(symbol)
        ]);
        tl.add(this.getRingAnimation(symbol), "-= 0.7");
        tl.add(() => this.resetNonActiveSymbols());
        return tl;
    }

    public darkenNonActiveSymbols(dur:number):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        for(let i:number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            if(i > this._currentIndex) {
                tl.add(symbol.getDarkenAnimation(dur), 0);
            }
        }
        return tl;
    }

    public resetNonActiveSymbols() {
        for(let i:number = 0; i < this._symbols.length; i++) {
            const symbol = this._symbols[i];
            if(i > this._currentIndex) {
                symbol.resetDarken();
            }
        }
    }

    public update(played:number, remaining:number) {
        this._currentIndex = played;

    }

    public getRingAnimation(symbol:FreeSpinIntroSymbol):TimelineLite {
        const tl:TimelineLite = new TimelineLite();
        this._ring.position.set(symbol.x, symbol.y);
        this._ring.tint = symbol.color;
        tl.add([
            () => this._ring.alpha = 1,
            () => this._ring.scale.set(0.8, 0.8)
        ]);

        tl.add(new TweenLite(this._ring.scale, 0.3, {x : 5.5, y : 5.5, ease : Linear.easeNone}));
        tl.add([
            () => this._ring.alpha = 0,
            () => this._ring.scale.set(0, 0)
        ]);

        return tl;
    }
}